import { CustomFieldElement, Deal, Contacts, ContractCategory } from 'models/Deal.d';
import { useScopes } from 'hooks/useScopes';
import {
  Avatar,
  Button,
  Card,
  Checkbox,
  Col,
  Comment,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  notification,
  Row,
  Select,
  Typography
} from 'antd';
import './DealModalSummary.scss';
import moment from 'moment-timezone';
import { EditOutlined, MailOutlined } from '@ant-design/icons';
import { useRecoilValue } from 'recoil';
import { myState, profileSwitchState } from 'recoil/atoms';
import { ANONYMOUS_CLIENT_EMAIL, ANONYMOUS_CLIENT_USER } from 'shared/constants';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSalesforceApi, Contact } from 'api/salesforce';
import { useEditDeal, useContractApi } from 'api/contract';
import { SfUserMetaData } from 'models/Deal';
import { round } from 'lodash-es';
import type { InputStatus } from 'antd/lib/_util/statusUtils';
import CategoryManager from 'components/category-management/CategoryManager';
import { useTagsApi } from 'api/useTags';
import TagsSelect from 'components/shared/TagsSelect';
import { renderSelectedTags, useTagKeyDown } from '../shared/TagsUtil';
import { useAccountSettingsApi } from 'api/accountSettings';
import { ACLCheck } from 'shared/Helpers';
import type React from 'react';

interface Props {
  deal?: Deal;
  closeModal?: () => void;
  refetchDeal?: () => void;
  onDeleteDeal?: (dealId: number) => void;
}

const defaultFieldsKeys = [
  'contract_start_date',
  'contract_renewal_date',
  'client_contact_id',
  'account_manager_id',
  'supplier',
  'subject',
  'term',
  'notice_period',
  'category',
  'tagIds',
  'includePrr'
];

/**
 * Represents a modal component used to display and edit deal summary information.
 * @component
 * @param {object} props - The props object containing deal data and event handlers.
 * @param {Deal} props.deal - The deal object containing deal information.
 * @param {Function} props.refetchDeal - The function to refetch deal data.
 * @returns {React.Component} The DealModalSummary component.
 */
const DealModalSummary: React.FC<Props> = ({ deal, refetchDeal }: Props): React.ReactElement => {
  // LOCAL STATE
  const [term, setTerm] = useState(1);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [isUserDealEditEnabled, setIsUserDealEditEnabled] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string>('');

  // ACL PERMISSIONS
  const { aclCheck } = useScopes();
  const [editSfMetaData, setEditSfMetaData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedSecondaryClientUserIds, setSelectedSecondaryClientUserIds] = useState<string[]>([]);
  const [selectedPrimaryClientUserId, setSelectedPrimaryClientUserId] = useState<string | null>(null);
  const [primaryContactStatus, setPrimaryContactStatus] = useState<InputStatus>('');
  const [npiUsers, setNpiUsers] = useState<any[]>([]);
  const [isTeemEnabled, setIsTeemEnabled] = useState<boolean>(false);
  const [selectedNPIAnalystId, setSelectedNPIAnalystId] = useState<string | null>(null);
  const isAnalyst = ACLCheck(['read:smartbench_access']);
  const lastContactsFetchKeyRef = useRef<string | number | 'self' | null>(null);
  const [isClientContactsLoading, setIsClientContactsLoading] = useState<boolean>(false);
  const [isNpiContactsLoading, setIsNpiContactsLoading] = useState<boolean>(false);

  // RECOIL STATE
  const profileSwitch = useRecoilValue(profileSwitchState);
  const me = useRecoilValue(myState);

  const [sfDealEditForm] = Form.useForm();
  const [userDealEditForm] = Form.useForm();
  const tcv = Form.useWatch('total_value', userDealEditForm);
  const annualValue = useMemo(() => {
    return deal?.total_value ? round(Number(deal.total_value) / (term || 1), 2) : 0;
  }, [deal, term]);

  // API
  const { getSfAccountContacts } = useSalesforceApi();
  const { updateDealMetadata } = useContractApi();
  const { getAllTags } = useTagsApi();
  const { getCurrentAccount } = useAccountSettingsApi();
  const handleTagKeyDown = useTagKeyDown();

  // Tags state
  const [tags, setTags] = useState<any[]>([]);
  const [allowUserTagCreation, setAllowUserTagCreation] = useState(false);

  const { mutate: editDeal, isLoading } = useEditDeal({
    onSuccess: () => {
      refetchDeal && refetchDeal();
      notification.success({
        message: 'Deal updated successfully'
      });
      setIsUserDealEditEnabled(false);
    },
    onError: () => {
      notification.error({
        message: 'Deal update failed'
      });
      userDealEditForm.resetFields(['subject', 'total_value', 'contract_start_date', 'contract_renewal_date']);
      setIsUserDealEditEnabled(false);
    }
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    !selectedClientId ? setPrimaryContactStatus('error') : setPrimaryContactStatus('');
  }, [selectedClientId]);

  useEffect(() => {
    if (tcv) {
      userDealEditForm.setFieldsValue({
        annual_value: annualValue
      });
    }
  }, [userDealEditForm, tcv, annualValue]);

  useEffect(() => {
    if (!deal) return;
    fetchTags();
    if (deal?.sfDeal?.Client_Contact__r?.Id) {
      setSelectedClientId(deal?.sfDeal?.Client_Contact__r?.Id);
    }
    if (deal?.secondaryContacts?.length > 0) {
      const userIds = deal.secondaryContacts.map((contact) => contact.user_id);
      setSelectedSecondaryClientUserIds(userIds);
    }
    // NPI Analyst selection will be mapped from Salesforce Id to salesforceUserId once users are loaded
    if (deal.contract_renewal_date && deal.contract_start_date) {
      setTerm(moment(deal?.contract_renewal_date).diff(deal?.contract_start_date, 'years', true));
    } else if (deal.projected_close_date && deal.projected_start_date) {
      setTerm(moment(deal?.projected_close_date).diff(deal?.projected_start_date, 'years', true));
    } else {
      setTerm(1);
    }
  }, [deal]);

  useEffect(() => {
    const selectedPrimaryContact = contacts.find((t) => t.Id === selectedClientId)?.user_id ?? null;
    setSelectedPrimaryClientUserId(selectedPrimaryContact);
  }, [selectedClientId, deal?.sfDeal?.Client_Contact__r?.Id, contacts]);

  const fetchTags = async () => {
    try {
      const profileId = me?.client?.id === deal?.client?.id ? undefined : deal?.client?.id;
      const tagsData = await getAllTags(profileId);
      if (tagsData) {
        setTags(tagsData);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  // Fetch client's contacts (for primary/secondary) based on deal's client profile
  const contactsFetchKey = useMemo(() => {
    if (!deal?.client?.id) return null;
    return me?.client?.id === deal?.client?.id ? 'self' : (deal?.client?.id as number | string);
  }, [deal?.client?.id, me?.client?.id]);

  useEffect(() => {
    if (!contactsFetchKey) return;
    if (lastContactsFetchKeyRef.current === contactsFetchKey) return;
    lastContactsFetchKeyRef.current = contactsFetchKey;
    (async () => {
      const profileId: number | undefined = contactsFetchKey === 'self' ? undefined : Number(contactsFetchKey);
      try {
        setIsClientContactsLoading(true);
        const [clientContacts, accountInfo] = await Promise.all([
          getSfAccountContacts(profileId),
          getCurrentAccount(profileId)
        ]);
        if (Array.isArray(clientContacts)) setContacts(clientContacts);
        if (accountInfo) {
          setAllowUserTagCreation(accountInfo?.allow_user_tag_creation || false);
          setIsTeemEnabled(Boolean((accountInfo as any)?.Current_Contract__r?.Teem_Enabled__c));
        }
      } catch (err) {
        console.error('Error occurred while fetching contacts', err);
      } finally {
        setIsClientContactsLoading(false);
      }
    })();
  }, [contactsFetchKey, getSfAccountContacts, getCurrentAccount]);

  // Fetch NPI users always for the NPI Delivery Contact dropdown
  const fetchedNpiContactsRef = useRef<boolean>(false);
  useEffect(() => {
    if (fetchedNpiContactsRef.current) return;
    fetchedNpiContactsRef.current = true;
    (async () => {
      try {
        setIsNpiContactsLoading(true);
        const users = await getSfAccountContacts();
        if (Array.isArray(users)) setNpiUsers(users);
      } catch (err) {
        console.error('Error occurred while fetching NPI users', err);
      } finally {
        setIsNpiContactsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!deal?.sfDeal?.NPI_Analyst__r?.Id) return;
    const match = npiUsers?.find((u) => u?.salesforceUserId === deal?.sfDeal?.NPI_Analyst__r?.Id);
    if (match) {
      setSelectedNPIAnalystId(match.salesforceUserId);
    }
  }, [deal?.sfDeal?.NPI_Analyst__r?.Id, npiUsers]);

  const getClientContactValue = useCallback(() => {
    return contacts?.find((i) => i?.Id === deal?.sfDeal?.Client_Contact__r?.Id)?.Name;
  }, [contacts, deal?.sfDeal?.Client_Contact__r?.Id]);

  const getClientSecondaryContactValues = useCallback(() => {
    return Array.from(
      new Set(
        contacts
          ?.filter((i) => deal?.secondaryContacts?.some((contact) => contact.user_id === i.user_id))
          .map((contact) => contact.user_id)
      )
    );
  }, [contacts, deal?.secondaryContacts]);

  const transformContractCategories = useCallback((contractCategories: ContractCategory[] | null): any[] => {
    if (!contractCategories || contractCategories.length === 0) {
      return [];
    }

    return contractCategories.map((category) => ({
      mainCategory: category.category_root_name,
      subCategory: category.category_prime_name,
      productCategories: category.category_second.map((second) => second.category_second_name),
      categoryRootId: category.category_root_id,
      categoryPrimeId: category.category_prime_id,
      categorySecondIds: category.category_second.map((second) => second.category_second_id)
    }));
  }, []);

  /**
   * Returns initial values for the SF Deal form.
   * If a deal exists and has SF Deal data, the function creates base fields with the following properties:
   * - contract_start_date: The Contract Start Date from the SF Deal, formatted as a moment object or an empty string.
   * - contract_renewal_date: The Contract Renewal Date from the SF Deal, formatted as a moment object or an empty string.
   * - client_contact_id: The client contact value obtained from the getClientContactValue() function or an empty string.
   *
   * For each custom field in the deal's client, the function adds a property to the base fields object with the following properties:
   * - key: The custom field key.
   * - value: The custom field value converted based on the data type (either as a moment object for date, boolean for boolean, or string for any other data type).
   *
   * The function returns the baseFields object if the deal and SF Deal data exist, otherwise returns an empty object.
   * @returns {object} - The initial values for the SF Deal form.
   * @param {object} deal - The deal from which the initial values will be extracted.
   * @param {Function} getClientContactValue - The function to retrieve the client contact value.
   */
  const sfDealFormInitialValues = useMemo(() => {
    if (deal && deal.sfDeal) {
      const baseFields = {
        contract_start_date: deal?.sfDeal?.Contract_Start_Date__c
          ? moment.utc(deal?.sfDeal?.Contract_Start_Date__c)
          : undefined,
        contract_renewal_date: deal?.sfDeal?.Contract_Renewal_Date__c
          ? moment.utc(deal?.sfDeal?.Contract_Renewal_Date__c)
          : undefined,
        client_contact_id: getClientContactValue() || '',
        account_manager_id: selectedNPIAnalystId || '',
        category: transformContractCategories(deal?.contract_category),
        tagIds: deal?.contract_tag?.map((tag) => tag.tagId) || [],
        notice_period: deal?.notice_period || undefined,
        includePrr: Boolean((deal?.sfDeal as any)?.Include_PRR__c)
      };
      deal.client.custom_field.forEach((field: CustomFieldElement) => {
        Object.assign(baseFields, {
          [field.key]:
            field.data_type === 'date' && field.custom_field_value[0]?.val
              ? moment(field.custom_field_value[0]?.val)
              : field.data_type === 'boolean'
              ? field.custom_field_value[0]?.val === 'true'
              : field.custom_field_value[0]?.val
        });
      });
      return baseFields;
    }
    return {};
  }, [deal, getClientContactValue, transformContractCategories, selectedNPIAnalystId]);

  const handleEditSfMetaData = () => {
    setEditSfMetaData(true);
  };

  const handleClientChange = (id: string) => {
    setSelectedClientId(id);
  };

  const handleSecondaryClientChange = (value: string[]) => {
    setSelectedSecondaryClientUserIds(value);
  };

  const handleNPIAnalystChange = (value: string) => {
    setSelectedNPIAnalystId(value);
  };

  const filteredSecondaryClientOptions = useMemo(() => {
    return contacts?.filter((contact) => contact?.Id !== selectedClientId);
  }, [contacts, selectedClientId]);

  const filteredPrimaryClientOptions = useMemo(() => {
    return contacts?.filter((contact) => !selectedSecondaryClientUserIds?.includes(contact?.user_id));
  }, [contacts, selectedSecondaryClientUserIds]);

  const clientOptions = useMemo(() => {
    return filteredPrimaryClientOptions?.map((a) => (
      <Select.Option key={a.Id} value={a.Id}>
        {a.Name}
      </Select.Option>
    ));
  }, [contacts, filteredPrimaryClientOptions]);

  const npiAnalystOptions = useMemo(() => {
    return npiUsers
      ?.filter((user) => Boolean(user?.salesforceUserId))
      .map((user) => (
        <Select.Option key={user.salesforceUserId} value={user.salesforceUserId}>
          {user.Name}
        </Select.Option>
      ));
  }, [npiUsers]);

  const renderEditButton = () => (
    <Col>
      {aclCheck(['update:contract_all', 'update:own_company_deals', 'update:site_wide_admin']) && (
        <Button
          type="link"
          size="small"
          icon={<EditOutlined />}
          onClick={() => {
            if (deal?.sfDeal) {
              handleEditSfMetaData();
            } else {
              setIsUserDealEditEnabled(true);
            }
          }}
          disabled={editSfMetaData || isUserDealEditEnabled || isClientContactsLoading || isNpiContactsLoading}
        >
          Edit Metadata
        </Button>
      )}
    </Col>
  );

  const handleSfDealSubmit = async (values: SfUserMetaData): Promise<void> => {
    if (primaryContactStatus == 'error') return;
    if (!selectedPrimaryClientUserId) {
      setPrimaryContactStatus('error');
      return;
    }
    const defaultFields: Record<string, any> = {};
    const customFields: Record<string, any> = {};
    Object.entries(values).forEach(([key, value]) => {
      if (defaultFieldsKeys.indexOf(key) > -1) {
        Object.assign(defaultFields, {
          [key]: value,
          client_contact_id: selectedClientId
        });
      } else {
        Object.assign(customFields, { [key]: value });
      }
    });

    // Transform category data to the required format
    if (defaultFields.category && Array.isArray(defaultFields.category)) {
      const categoryIds = defaultFields.category.map((category: any) => ({
        categoryRootId: category.categoryRootId,
        categoryPrimeId: category.categoryPrimeId,
        categorySecondIds: category.categorySecondIds
      }));
      defaultFields['categoryIds'] = categoryIds;
      delete defaultFields.category; // Remove the original category field
    }
    // Transform tag data to separate existing tags and new tags
    if (defaultFields.tagIds && Array.isArray(defaultFields.tagIds)) {
      const newTagIds = defaultFields.tagIds.filter((tag: any) => typeof tag === 'string').map((tag: any) => tag);
      defaultFields.newTags = newTagIds
        .map((tagId: string) => {
          const foundIdx = tags.findIndex((tag: any) => tag.id === tagId);
          if (foundIdx !== -1) {
            return {
              name: tags[foundIdx].name,
              color: tags[foundIdx].color
            };
          }
        })
        .filter((tag: any) => tag); // Remove undefined values
      defaultFields.tagIds = defaultFields.tagIds.filter((tag: any) => typeof tag === 'number').map((tag: any) => tag);
    }

    defaultFields['primaryContactUserId'] = selectedPrimaryClientUserId || '';
    defaultFields['secondaryContactUserIds'] =
      selectedSecondaryClientUserIds?.length > 0 ? selectedSecondaryClientUserIds : [];
    defaultFields['analystSalesforceId'] = selectedNPIAnalystId || '';

    try {
      setLoading(true);
      const payload = { defaultFields, customFields };
      const res = await updateDealMetadata(deal?.id as number, payload);
      if (res?.id) {
        notification.success({
          message: 'Data updated successfully',
          duration: 4
        });
        refetchDeal && refetchDeal();
      }
    } catch (error) {
      notification.error({
        message: 'Failed to update data',
        duration: 4
      });
    } finally {
      setLoading(false);
      setEditSfMetaData(false); // Always switch back to view mode
    }
  };

  const resetForm = () => {
    const customFieldsKey = deal?.client?.custom_field.map((field: CustomFieldElement) => field?.key);
    setPrimaryContactStatus('');
    deal?.sfDeal?.Client_Contact__r?.Id && setSelectedClientId(deal?.sfDeal?.Client_Contact__r?.Id);
    setSelectedSecondaryClientUserIds(deal?.secondaryContacts?.map((contact) => contact?.user_id) || []);
    if (deal?.sfDeal?.NPI_Analyst__r?.Id) {
      const match = npiUsers?.find((u) => u?.Id === deal?.sfDeal?.NPI_Analyst__r?.Id && u?.salesforceUserId);
      setSelectedNPIAnalystId(match?.salesforceUserId || null);
    } else {
      setSelectedNPIAnalystId(null);
    }
    if (editSfMetaData) {
      setEditSfMetaData(false);
      const sfDealEditFormFields = [
        'contract_start_date',
        'contract_renewal_date',
        'client_contact_id',
        'account_manager_id',
        'category',
        'tagIds',
        'includePrr'
      ];
      sfDealEditForm.resetFields(
        customFieldsKey && customFieldsKey?.length > 0
          ? sfDealEditFormFields.concat(customFieldsKey)
          : sfDealEditFormFields
      );
    }
  };

  /**
   * Renders a custom field based on its data type.
   * @param {CustomFieldElement} field - The custom field object.
   * @returns {React.ReactElement} - The rendered custom field component.
   */
  const customField = (field: CustomFieldElement): React.ReactElement => {
    const required: boolean | undefined = field.attributes.required;

    switch (field.data_type) {
      case 'string':
        return (
          <Form.Item
            key={field.id}
            label={field.label}
            name={field.key}
            rules={[{ required: required, message: 'A value is required' }]}
          >
            <Input size="large" showCount={editSfMetaData} allowClear {...field.attributes} />
          </Form.Item>
        );
      case 'number': {
        const numberAttributes: any = { ...field?.attributes };
        delete numberAttributes['required'];
        return (
          <Form.Item
            key={field.id}
            label={field.label}
            name={field.key}
            rules={[
              {
                required: field?.attributes?.required,
                message: `${field?.label} is required`
              },
              {
                validator: (_, value) => {
                  const minValue = field?.attributes?.min;
                  const maxValue = field?.attributes?.max;

                  if (value === undefined || value === null || value === '') {
                    return Promise.resolve();
                  }

                  if (minValue && Number(value) < Number(minValue)) {
                    return Promise.reject(new Error(`Minimum value should be ${minValue}.`));
                  }

                  if (maxValue && Number(value) > Number(maxValue)) {
                    return Promise.reject(new Error(`Maximum value should be ${maxValue}.`));
                  }

                  return Promise.resolve();
                }
              }
            ]}
          >
            <InputNumber<string>
              size="large"
              style={{ width: 270 }}
              className="rounded-md"
              {...numberAttributes}
              controls={true}
            />
          </Form.Item>
        );
      }
      case 'date':
        return (
          <Form.Item
            key={field.id}
            label={field.label}
            name={field.key}
            rules={[{ required: required, message: 'A value is required' }]}
          >
            <DatePicker size="large" {...field.attributes} />
          </Form.Item>
        );
      case 'boolean':
        return (
          <Form.Item key={field.id} label={field.label} name={field.key} valuePropName="checked">
            <Checkbox />
          </Form.Item>
        );
      default:
        return (
          <Form.Item
            key={field.id}
            label={field.label}
            name={field.key}
            rules={[{ required: required, message: 'A value is required' }]}
          >
            <Input size="large" showCount={editSfMetaData} allowClear {...field.attributes} />
          </Form.Item>
        );
    }
  };

  const renderSfDealForm = () => {
    return (
      <Form
        className={editSfMetaData ? '' : 'view-mode'}
        form={sfDealEditForm}
        labelCol={{ span: 8 }}
        labelAlign="left"
        labelWrap
        size="small"
        wrapperCol={{ span: 16 }}
        colon={false}
        disabled={!editSfMetaData}
        initialValues={sfDealFormInitialValues}
        onFinish={handleSfDealSubmit}
      >
        <Form.Item label="Supplier" name="supplier">
          <Typography.Text>{deal?.vendor.name}</Typography.Text>
        </Form.Item>
        {deal?.reseller?.name && (
          <Form.Item label="Reseller" name="reseller">
            <Typography.Text>{deal?.reseller?.name || '--'}</Typography.Text>
          </Form.Item>
        )}
        <Form.Item label="Subject" name="subject">
          <Typography.Text>{deal?.subject || deal?.name}</Typography.Text>
        </Form.Item>
        <Form.Item label="Situation Summary">
          <Input.TextArea disabled value={deal?.sf_description || ''} bordered={true} rows={3} />
        </Form.Item>
        <Form.Item label="Term" name="term">
          <Typography.Text>{deal && deal?.sfDeal?.Term__c ? deal?.sfDeal.Term__c : undefined}</Typography.Text>
        </Form.Item>
        <Form.Item label="Notice Period" name="notice_period">
          {editSfMetaData ? (
            <InputNumber
              size="large"
              className="rounded-md"
              placeholder="e.g., 30, 60, 90"
              style={{ width: '100%' }}
              min={0}
              precision={0}
              addonAfter="days"
            />
          ) : (
            <Typography.Text>{deal?.notice_period != null ? `${deal.notice_period} days` : '--'}</Typography.Text>
          )}
        </Form.Item>
        <Form.Item label="Contract Start Date" name="contract_start_date">
          <DatePicker
            size="large"
            className="rounded-md"
            format={'MMM DD, YYYY'}
            disabledDate={(current) => {
              const endDate = sfDealEditForm.getFieldValue('contract_renewal_date');
              return current && endDate && current.isAfter(endDate);
            }}
          />
        </Form.Item>
        <Form.Item label="Contract End Date" name="contract_renewal_date">
          <DatePicker
            size="large"
            className="rounded-md"
            format={'MMM DD, YYYY'}
            disabledDate={(current) => {
              const endDate = sfDealEditForm.getFieldValue('contract_start_date');
              return current && endDate && current.isBefore(endDate);
            }}
          />
        </Form.Item>
        <Form.Item name="category" label="Category">
          <CategoryManager
            profile_id={profileSwitch ? deal?.client?.id || null : null}
            form={sfDealEditForm}
            readOnly={!editSfMetaData}
          />
        </Form.Item>
        {editSfMetaData ? (
          <Form.Item name="tagIds" label="Tags">
            <TagsSelect
              tags={tags}
              disabled={!editSfMetaData}
              onKeyDown={(e) =>
                allowUserTagCreation &&
                handleTagKeyDown(e, sfDealEditForm, profileSwitch?.id, fetchTags, 'tagIds', setTags)
              }
              allowUserTagCreation={allowUserTagCreation}
            />
          </Form.Item>
        ) : (
          <Form.Item label="Tags">{renderSelectedTags(deal?.contract_tag || [])}</Form.Item>
        )}
        <Form.Item label="Deal Type">
          <Typography.Text>{deal?.sfDeal?.Deal_Reason__c}</Typography.Text>
        </Form.Item>
        <Form.Item label="Report Type">
          <Typography.Text>{deal?.sfDeal?.Report_Type__c}</Typography.Text>
        </Form.Item>
        {isTeemEnabled && (
          <Form.Item name="includePrr" valuePropName="checked" label="Include a Teem Product Redundancy Report">
            <Checkbox />
          </Form.Item>
        )}
        {deal && deal.client.custom_field.length > 0 && (
          <>
            <Divider className="uppercase text-sm" orientation="left">
              Custom Fields
            </Divider>
            {deal.client.custom_field.map(customField)}
          </>
        )}
      </Form>
    );
  };

  const renderDeliveryContact = () => {
    const analystName = deal?.sfDeal?.NPI_Analyst__r?.Name;

    return (
      <>
        <div className="text-sm font-bold">NPI Delivery Contact</div>
        {isAnalyst && editSfMetaData ? (
          <Select
            className="w-full"
            placeholder={'Select NPI Delivery Contact'}
            size="large"
            allowClear
            showArrow
            virtual={false}
            value={selectedNPIAnalystId || undefined}
            onChange={handleNPIAnalystChange}
            showSearch={true}
            optionFilterProp="children"
          >
            {npiAnalystOptions}
          </Select>
        ) : (
          <Comment
            avatar={
              <Avatar className="bg-[#152d51]">
                {deal?.sfDeal?.NPI_Analyst__r
                  ? deal?.sfDeal.NPI_Analyst__r.FirstName.charAt(0) + deal?.sfDeal.NPI_Analyst__r.LastName.charAt(0)
                  : 'NPI'}
              </Avatar>
            }
            author={analystName || '--'}
            content={
              <>
                <MailOutlined /> {deal?.sfDeal?.NPI_Analyst__r?.Email || '--'}
              </>
            }
          />
        )}
      </>
    );
  };

  const renderClientSuccessContact = () => {
    const clientSuccessName = deal?.sfDeal?.Account_Manager__r?.Name;

    return (
      <>
        <div className="text-sm font-bold">NPI Client Success Contact</div>
        <Comment
          avatar={
            <Avatar className="bg-[#152d51]">
              {deal?.sfDeal?.Account_Manager__r
                ? deal?.sfDeal.Account_Manager__r.FirstName.charAt(0) +
                  deal?.sfDeal.Account_Manager__r.LastName.charAt(0)
                : 'NPI'}
            </Avatar>
          }
          author={clientSuccessName || '--'}
          content={
            <>
              <MailOutlined /> {deal?.sfDeal?.Account_Manager__r?.Email || '--'}
            </>
          }
        />
      </>
    );
  };

  const renderPrimaryClientContact = () => (
    <>
      <div className="text-base font-bold">Primary Contact</div>
      {editSfMetaData ? (
        <>
          <Select
            className="w-full"
            placeholder={'Select Primary Contact'}
            size="large"
            allowClear
            showArrow
            virtual={false}
            defaultValue={getClientContactValue()}
            onChange={handleClientChange}
            showSearch={true}
            status={primaryContactStatus}
            optionFilterProp="children"
          >
            {clientOptions}
          </Select>
          {primaryContactStatus == 'error' && <span className="text-red-500">Primary contact is required</span>}
        </>
      ) : (
        <Comment
          avatar={
            <Avatar className="bg-[#30aa9e]">
              {deal?.sfDeal?.Client_Contact__r
                ? deal?.sfDeal.Client_Contact__r.FirstName.charAt(0) + deal?.sfDeal.Client_Contact__r.LastName.charAt(0)
                : 'NPI'}
            </Avatar>
          }
          author={
            deal?.sfDeal?.Client_Contact__r
              ? profileSwitch && profileSwitch.anonymous
                ? ANONYMOUS_CLIENT_USER
                : deal?.sfDeal.Client_Contact__r.Name
              : '--'
          }
          content={
            <>
              <MailOutlined />{' '}
              {deal?.sfDeal?.Client_Contact__r
                ? profileSwitch && profileSwitch.anonymous
                  ? ANONYMOUS_CLIENT_EMAIL
                  : deal?.sfDeal.Client_Contact__r.Email
                : '--'}
            </>
          }
        />
      )}
    </>
  );

  const renderSecondaryClientContact = () => (
    <>
      <div className="text-base font-bold">Secondary Contact</div>
      {editSfMetaData ? (
        <Select
          className="w-full"
          mode="multiple"
          placeholder="Select Secondary Contacts"
          size="large"
          allowClear
          showArrow
          virtual={false}
          defaultValue={getClientSecondaryContactValues()}
          onChange={handleSecondaryClientChange}
          showSearch={true}
          fieldNames={{ label: 'Name', value: 'user_id' }}
          options={filteredSecondaryClientOptions}
          optionFilterProp="Name"
        />
      ) : (
        <div className="max-h-[200px] overflow-y-auto">
          {deal?.secondaryContacts?.map((contact: Contacts) => (
            <Comment
              key={contact.user_id}
              avatar={
                <Avatar className="bg-[#30aa9e]">
                  {contact.FirstName.charAt(0).toUpperCase()}
                  {contact.LastName.charAt(0).toUpperCase()}
                </Avatar>
              }
              author={profileSwitch && profileSwitch.anonymous ? ANONYMOUS_CLIENT_USER : contact.Name}
              content={
                <>
                  <MailOutlined /> {profileSwitch && profileSwitch.anonymous ? ANONYMOUS_CLIENT_EMAIL : contact.Email}
                </>
              }
            />
          ))}
        </div>
      )}
    </>
  );

  return (
    <div className="deal-modal-summary">
      <Card className="pt-4">
        <Row gutter={36}>
          <Col span={16}>
            <Row>
              <Col flex="auto">
                <Typography.Title level={4}>Summary</Typography.Title>
              </Col>
              {renderEditButton()}
            </Row>
            {deal && deal.sfDeal ? renderSfDealForm() : null}
          </Col>
          <Col span={8}>
            {(deal?.sfDeal && (
              <Card bordered={true} className={'contacts-card-body'} title="Key Contacts">
                {renderDeliveryContact()}
                <Divider />
                {renderClientSuccessContact()}
                <Divider />
                {renderPrimaryClientContact()}
                <Divider />
                {renderSecondaryClientContact()}
              </Card>
            )) ||
              null}
          </Col>
        </Row>
      </Card>

      {editSfMetaData ? (
        <Row justify="end" className="footer-cta">
          <Col>
            <Button onClick={() => resetForm()}>Cancel</Button>
            <Button
              loading={loading || isLoading}
              type={'primary'}
              htmlType="submit"
              className="ml-4"
              onClick={() => sfDealEditForm.submit()}
            >
              Save
            </Button>
          </Col>
        </Row>
      ) : null}
    </div>
  );
};

export default DealModalSummary;
